import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO_URL, waLink } from "../lib/data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";
  const light = onHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled
          ? "bg-brand-bg/80 backdrop-blur-xl border-b border-black/5 py-3"
          : light
          ? "bg-brand-forest/20 backdrop-blur-md border-b border-white/10 py-5"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 shrink-0">
          <img src={LOGO_URL} alt="40X Leaves" className="h-10 md:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                  light
                    ? active
                      ? "text-white"
                      : "text-white/70 hover:text-white"
                    : active
                    ? "text-brand-forest"
                    : "text-brand-muted hover:text-brand-ink"
                }`}
              >
                {link.label}
                {active && (
                  <motion.span
                    layoutId="nav-underline"
                    className={`absolute left-4 right-4 -bottom-0.5 h-px ${light ? "bg-white" : "bg-brand-sprout"}`}
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink("Hi 40X Leaves, I'd like to subscribe. My pincode is ")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-cta"
            className={`hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[transform,background-color,border-color] duration-300 hover:scale-105 ${
              light
                ? "bg-white/15 backdrop-blur-md border border-white/30 text-white hover:bg-brand-sprout hover:border-brand-sprout"
                : "bg-brand-forest text-brand-bg hover:bg-brand-sprout"
            }`}
          >
            Subscribe on WhatsApp
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 ${light ? "text-white" : "text-brand-ink"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-brand-bg/95 backdrop-blur-xl border-t border-black/5"
          >
            <div className="px-5 py-4 flex flex-col">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className="py-3 text-lg font-serif text-brand-ink border-b border-black/5 last:border-0"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={waLink("Hi 40X Leaves, I'd like to subscribe. My pincode is ")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex justify-center rounded-full bg-brand-forest text-brand-bg px-5 py-3 text-sm font-semibold"
              >
                Subscribe on WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
